import type { SwaggerResponse } from '@/services/config';
import { Http } from '@/services/httpRequest';
import {
    ProviderAccountsController_saveAccount,
    ProviderConfigActionsController_listClarifyCatalog,
    ProviderConfigActionsController_testProviderConfig,
    ProviderConfigController_saveProviderConfig,
    template,
} from '@/services/services';
import type {
    ClarifyCatalogDto,
    ClarifyCatalogRequestDto,
    LocationProviderConfigDto,
    PenPatchDto,
    ProviderAccountDto,
    SaveProviderAccountRequestDto,
    SaveProviderConfigRequestDto,
    TestPenRequestDto,
    TestProviderConfigRequestDto,
    TestProviderConfigResultDto,
} from '@/services/types';

import type { SecretName } from './types';

/**
 * Hand-written fetchers for the four requests that carry secrets (save, test, catalog and
 * account save), as domain/feedingDashboard/api.ts does for its explicit-null clears.
 * swagger-typescript turns a `nullable` property into an optional one, so the generated
 * `SecretsPatchDto` has no room for `null`; here `null` clears a stored secret and a name left
 * out keeps it, which are not the same request. The URLs come from the generated services, so
 * they follow the spec. Every other provider-config request goes through the generated hooks.
 */

/** A typed secret, `null` to clear the stored one; a name left out keeps it unchanged. */
export type SecretsPatchWire = Partial<Record<SecretName, string | null>>;

type WithWireSecrets<T> = Omit<T, 'secrets'> & { secrets?: SecretsPatchWire };

export type SaveProviderConfigRequestWire = WithWireSecrets<
    Omit<SaveProviderConfigRequestDto, 'pens'>
> & { pens: WithWireSecrets<PenPatchDto>[] };

export type TestProviderConfigRequestWire = WithWireSecrets<
    Omit<TestProviderConfigRequestDto, 'pens'>
> & { pens: WithWireSecrets<TestPenRequestDto>[] };

export type ClarifyCatalogRequestWire = WithWireSecrets<ClarifyCatalogRequestDto>;

export type SaveProviderAccountRequestWire = WithWireSecrets<SaveProviderAccountRequestDto>;

// What the generated client sends: the backend's JSON parser reads only this content type.
const JSON_REQUEST = {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
};

export interface SiteRequestVariables<TBody> {
    locationId: number;
    providerName: string;
    requestBody: TBody;
}

export function saveProviderConfigRequest(
    variables: SiteRequestVariables<SaveProviderConfigRequestWire>
): Promise<SwaggerResponse<LocationProviderConfigDto>> {
    const { locationId, providerName, requestBody } = variables;
    return Http.putRequest(
        template(ProviderConfigController_saveProviderConfig.key, { locationId, providerName }),
        undefined,
        requestBody,
        undefined,
        JSON_REQUEST
    );
}

export function testProviderConfigRequest(
    variables: SiteRequestVariables<TestProviderConfigRequestWire>
): Promise<SwaggerResponse<TestProviderConfigResultDto>> {
    const { locationId, providerName, requestBody } = variables;
    return Http.postRequest(
        template(ProviderConfigActionsController_testProviderConfig.key, {
            locationId,
            providerName,
        }),
        undefined,
        requestBody,
        undefined,
        JSON_REQUEST
    );
}

export function listClarifyCatalogRequest(variables: {
    locationId: number;
    requestBody: ClarifyCatalogRequestWire;
}): Promise<SwaggerResponse<ClarifyCatalogDto>> {
    const { locationId, requestBody } = variables;
    return Http.postRequest(
        template(ProviderConfigActionsController_listClarifyCatalog.key, { locationId }),
        undefined,
        requestBody,
        undefined,
        JSON_REQUEST
    );
}

export function saveProviderAccountRequest(variables: {
    providerName: string;
    requestBody: SaveProviderAccountRequestWire;
}): Promise<SwaggerResponse<ProviderAccountDto[]>> {
    const { providerName, requestBody } = variables;
    return Http.putRequest(
        template(ProviderAccountsController_saveAccount.key, { providerName }),
        undefined,
        requestBody,
        undefined,
        JSON_REQUEST
    );
}
